/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import qs from 'qs';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSync, faTrash } from '@fortawesome/pro-solid-svg-icons';

import {
  fetchServicePlan,
  selectServicePlan,
  selectBillingLoading,
} from '../../../redux/slices/billingSlice/billingSlice';
import envConfig from '../../../envConfig';

const GuestBilling = () => {
  const dispatch = useDispatch();
  const servicePlan = useSelector(selectServicePlan);
  const reduxLoading = useSelector(selectBillingLoading);

  const subscriptionId = servicePlan && servicePlan.subscriptionId;
  const guestBillingHash = servicePlan && servicePlan.guestBillingHash;
  const guestBillingLink = guestBillingHash
    ? `${envConfig.raaUrl}/guest/billing/auth/${guestBillingHash}`
    : '';

  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  // Once redux loading finishes, clear local busy
  useEffect(() => {
    if (!reduxLoading) setBusy(false);
  }, [reduxLoading]);

  const loading = busy || reduxLoading;

  const copyToClipboard = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(guestBillingLink);
      } else {
        const temp = document.createElement('textarea');
        temp.value = guestBillingLink;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      setError('Unable to copy link to clipboard.');
    }
  };

  const generateLink = async () => {
    setError(null);
    setBusy(true);
    try {
      await axios.post(
        `${envConfig.raaUrl}/subscription/data/generate-new-guest-billing-link`,
        qs.stringify({ subscriptionId }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          },
          withCredentials: true,
        },
      );
      dispatch(fetchServicePlan());
    } catch (e) {
      setError('Unexpected error while trying to create guest billing link.');
      setBusy(false);
    }
  };

  const deleteLink = async () => {
    setError(null);
    setBusy(true);
    try {
      await axios.post(
        `${envConfig.raaUrl}/subscription/data/delete-guest-billing-link`,
        qs.stringify({ subscriptionId }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          },
          withCredentials: true,
        },
      );
      dispatch(fetchServicePlan());
    } catch (e) {
      setError('Unexpected error while trying to delete guest billing link.');
      setBusy(false);
    }
  };

  const refreshLink = () => generateLink();

  if (!subscriptionId) return null;

  return (
    <div className="guest-billing">
      {!guestBillingLink ? (
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={generateLink}
          disabled={loading}
        >
          {loading ? (
            <span className="spinner-border spinner-border-sm mr-2" />
          ) : (
            <FontAwesomeIcon icon={faUser} className="mr-2" />
          )}
          {loading ? 'Enabling...' : 'Enable Guest Billing'}
        </button>
      ) : (
        <div
          className="d-flex align-items-center p-2"
          style={{ maxWidth: '360px' }}
        >
          {loading ? (
            <span className="spinner-border spinner-border-sm mr-2" />
          ) : (
            <FontAwesomeIcon icon={faUser} className="mr-2" />
          )}
          <span className="mr-2 text-muted small text-nowrap">Guest Billing Link</span>
          <input
            type="text"
            className="form-control form-control-sm mr-2"
            value={copied ? 'Copied!' : guestBillingLink}
            readOnly
            onClick={copyToClipboard}
            style={{ cursor: 'pointer', textOverflow: 'ellipsis' }}
            title="Click to copy"
          />
          <button
            type="button"
            className="btn btn-link btn-sm p-1 text-primary"
            onClick={refreshLink}
            disabled={loading}
            title="Generate a new link"
            aria-label="Refresh guest billing link"
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
          <button
            type="button"
            className="btn btn-link btn-sm p-1 text-primary"
            onClick={deleteLink}
            disabled={loading}
            title="Delete guest billing link"
            aria-label="Delete guest billing link"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      )}

      {error && (
        <div className="text-danger mt-2 small">{error}</div>
      )}
    </div>
  );
};

export default GuestBilling;
